import { useEffect, useRef, useState } from 'react';
import type { ShopUnbindBlockerDTO, ShopUnbindBlockerKind, ShopUnbindBlockersDTO } from '@wberp/shared';
import { api } from './api';
import { ErrorBox, formatDate, Loading, Modal } from './components';
import './shop-unbind-blockers.css';

const KIND: Record<ShopUnbindBlockerKind, string> = {
  job: '发布任务', card_member: '建卡成员', card_batch: '建卡批次', external_operation: '平台操作待确认',
  quota_claim: '额度预留', quota_period: '额度汇总', execution_slot: '执行租约', cleanup: '商品清理',
};

function localLink(href: string | null): string | undefined {
  if (!href) return undefined;
  return /^\/(?:products\/[^/?#]+|batches\/[^/?#]+|publish-queue\?productId=[a-f\d-]{36}|shops\?cleanupRunId=[a-f\d-]{36})$/iu.test(href) ? href : undefined;
}

type Props = { shopId: string; shopName: string; scopeKey: string; onClose: () => void };

export function ShopUnbindBlockersDialog(props: Props) {
  return <BlockersDialog key={JSON.stringify([props.scopeKey, props.shopId])} {...props} />;
}

function BlockersDialog({ shopId, shopName, onClose }: Props) {
  const [data, setData] = useState<ShopUnbindBlockersDTO | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const alive = useRef(true);
  const requestVersion = useRef(0);
  const cursor = cursors[page];

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; requestVersion.current++; };
  }, []);

  useEffect(() => {
    const version = ++requestVersion.current;
    setLoading(true); setError(null); setData(null); setConfirmKey(null);
    void api.getShopUnbindBlockers(shopId, cursor).then((value) => {
      if (alive.current && requestVersion.current === version) setData(value);
    }).catch((reason: unknown) => {
      if (alive.current && requestVersion.current === version) setError(reason instanceof Error ? reason : new Error('读取解绑阻塞明细失败'));
    }).finally(() => {
      if (alive.current && requestVersion.current === version) setLoading(false);
    });
    return () => { requestVersion.current++; };
  }, [shopId, cursor, revision]);

  function refresh(): void {
    setPage(0); setCursors([undefined]); setConfirmKey(null); setRevision((value) => value + 1);
  }

  async function stop(item: ShopUnbindBlockerDTO): Promise<void> {
    if (!item.stopAction || item.restricted || busy || loading || error || confirmKey !== item.key) return;
    const version = requestVersion.current;
    setBusy(true); setError(null); setMessage(null);
    try {
      const result = await api.stopShopUnbindBlocker(shopId, item.stopAction);
      if (!alive.current || requestVersion.current !== version) return;
      setMessage(`${result.message}${result.pending ? ' 后台会继续核对平台回执，可刷新查看进度；无需等待此项才能解绑。' : ' 已重新读取最新明细；可直接返回并立即解绑封存。'}`);
      refresh();
    } catch (reason) {
      if (alive.current && requestVersion.current === version) {
        setError(reason instanceof Error ? reason : new Error('停止失败，请刷新明细后重试'));
        setConfirmKey(null);
      }
    } finally {
      if (alive.current && requestVersion.current === version) setBusy(false);
    }
  }

  return <Modal title="店铺解绑阻塞明细" description={shopName} wide className="shop-unbind-blockers" onClose={() => { if (!busy) onClose(); }} actions={<>
    <button className="button" disabled={busy || loading} onClick={refresh}>刷新明细</button>
    <button className="button" disabled={busy} onClick={onClose}>返回解绑</button>
  </>}>
    <div className="unbind-blockers-body">
      <div className="notice">这里是可选诊断明细，不是解绑前置条件。确认后会立即解除绑定并封存旧任务，无需逐项安全停止或等待任务完成。重新绑定同公司同卖家后，后台会分批核对恢复；未知回执会保留核对。</div>
      {message && <div className="success-box" role="status">{message}</div>}
      {error && <><ErrorBox error={error} onRetry={refresh} /><p className="field-help">任务状态可能已经变化，或明细暂不可用。请刷新后查看最新诊断；停止失败不影响店铺立即解绑，已发送操作仍会保留平台回执核对。</p></>}
      {loading ? <Loading label="正在读取解绑阻塞明细…" /> : data && <>
        {data.request && <div className="notice" role="status"><strong>解绑处理中。</strong>请求时间：{formatDate(data.request.requestedAt)}。{data.request.message} 后台会自动继续处理，无需逐条手动停止。</div>}
        <div className="unbind-blockers-summary" role="status">
          <strong>诊断记录 {data.total} 条</strong>
          <span className="field-help">不同类型记录不能当作商品数量。</span>
          {data.counts ? <>
            <span>关联商品 {data.counts.products}（含历史）</span>
            <span>待停止发布任务 {data.counts.publishJobs}</span>
            <span>建卡批次 {data.counts.cardBatches}（含历史）</span>
            <span>未发送历史批次 {data.counts.unsentCardBatches}</span>
            <span>WB待确认操作 {data.counts.platformOperations}</span>
          </> : <span className="field-help">旧版响应未提供分项数量；不能根据诊断记录总数推算商品数。</span>}
          {data.summary.map((entry) => <span key={entry.kind}>{KIND[entry.kind]}诊断记录 {entry.count} 条</span>)}
        </div>
        {data.unbound ? <p>该店铺已经解绑，请返回并刷新店铺列表。</p> : !data.total ? <p>{data.request ? '当前没有待处理诊断记录，后台仍会继续核对已封存旧任务。' : '当前未发现诊断记录；可直接返回并立即解绑封存，服务端仍会核验最新状态。'}</p> : <div className="unbind-blocker-list">{data.items.map((item) => {
          const productHref = localLink(item.productHref);
          const href = localLink(item.href);
          return <article className="unbind-blocker" key={item.key}>
            <div className="unbind-blocker-heading"><strong>{KIND[item.kind]}</strong>{!item.restricted && <span className="text-muted">{item.status}</span>}</div>
            {item.restricted ? <><p>其他员工的阻塞记录，详情已隐藏。</p><p className="field-help">请联系公司管理员或任务所属员工处理，再刷新明细。</p></> : <>
              {(item.productTitle || item.productId) && <div className="unbind-blocker-product"><strong>{item.productTitle || '关联商品'}</strong>{item.productId && <span>商品 ID：{item.productId}</span>}{item.sourceProductId && <span>1688 商品 ID：{item.sourceProductId}</span>}</div>}
              <dl className="unbind-blocker-identifiers">{item.jobId && <><dt>任务 ID</dt><dd>{item.jobId}</dd></>}{item.targetId && <><dt>发布目标 ID</dt><dd>{item.targetId}</dd></>}{item.publishBatchId && <><dt>批次 ID</dt><dd>{item.publishBatchId}</dd></>}{item.cleanupRunId && <><dt>清理运行 ID</dt><dd>{item.cleanupRunId}</dd></>}{!item.jobId && !item.publishBatchId && !item.cleanupRunId && item.recordId && <><dt>记录 ID</dt><dd>{item.recordId}</dd></>}{item.quantity !== null && <><dt>数量</dt><dd>{item.quantity}</dd></>}{item.periodId && <><dt>额度期间</dt><dd>{item.periodId}</dd></>}</dl>
              <p><strong>阻塞原因：</strong>{item.reason}</p><p className="field-help"><strong>下一步：</strong>{item.guidance}</p>
              <div className="unbind-blocker-actions">{productHref && <a className="button button-small" href={productHref}>查看商品</a>}{href && href !== productHref && <a className="button button-small" href={href}>{item.kind === 'cleanup' ? '查看清理记录' : '查看相关任务'}</a>}{item.stopAction && <button className="button button-small button-danger" disabled={busy || Boolean(error)} onClick={() => setConfirmKey(item.key)}>安全停止</button>}</div>
              {confirmKey === item.key && <div className="unbind-blocker-confirm" role="group" aria-label="确认安全停止"><p>确认停止此项尚未发出的工作？已提交或结果未知的操作会继续保留回执核对。此操作是可选的单项处理，不影响立即解绑或后台恢复核对。</p><div className="unbind-blocker-actions"><button className="button button-small" disabled={busy} onClick={() => setConfirmKey(null)}>暂不停止</button><button className="button button-small button-danger" disabled={busy} onClick={() => void stop(item)}>{busy ? '处理中…' : '确认安全停止'}</button></div></div>}
            </>}
          </article>;
        })}</div>}
        <div className="unbind-blockers-pagination"><button className="button button-small" disabled={busy || page === 0} onClick={() => { setConfirmKey(null); setPage((value) => value - 1); }}>上一页</button><span>第 {page + 1} 页 · 每页最多 30 项</span><button className="button button-small" disabled={busy || !data.nextCursor} onClick={() => { if (!data.nextCursor) return; setConfirmKey(null); setCursors((current) => [...current.slice(0, page + 1), data.nextCursor]); setPage((value) => value + 1); }}>下一页</button></div>
      </>}
    </div>
  </Modal>;
}
